/**
 * Proyecta planos de corte (IPP/IOP del stack volumétrico) sobre el plano Scout.
 */

function parseFloatArray(raw, expectedMin) {
  if (Array.isArray(raw)) {
    const nums = raw.map((v) => parseFloat(v)).filter((n) => Number.isFinite(n))
    return nums.length >= expectedMin ? nums : null
  }
  if (raw == null || typeof raw !== 'string') return null
  const parts = raw.split('\\').map((v) => parseFloat(v))
  if (parts.length < expectedMin || parts.some((n) => Number.isNaN(n))) return null
  return parts
}

function cross(a, b) {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ]
}

function dot(a, b) {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2]
}

function sub(a, b) {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]]
}

function add(a, b) {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]]
}

function scale(a, s) {
  return [a[0] * s, a[1] * s, a[2] * s]
}

function norm(a) {
  const L = Math.sqrt(dot(a, a)) || 1
  return scale(a, 1 / L)
}

/**
 * Lee geometría de un file item o imagen Cornerstone.
 * @param {object} fileOrImage
 * @returns {{ ipp: number[], iop: number[], rows: number, cols: number, spacing: number[] }|null}
 */
export function readPlaneGeometry(fileOrImage) {
  if (!fileOrImage) return null
  const image = fileOrImage.image || fileOrImage
  const fromFile = fileOrImage.imagePositionPatient
    ? fileOrImage
    : null

  let ipp =
    parseFloatArray(
      (fromFile && fromFile.imagePositionPatient) ||
        (fileOrImage.imagePositionPatient),
      3
    ) || null
  let iop =
    parseFloatArray(
      (fromFile && fromFile.imageOrientationPatient) ||
        fileOrImage.imageOrientationPatient,
      6
    ) || null
  let spacing =
    parseFloatArray(
      (fromFile && fromFile.pixelSpacing) || fileOrImage.pixelSpacing,
      2
    ) || null

  if ((!ipp || !iop) && image && image.data && typeof image.data.string === 'function') {
    try {
      if (!ipp) ipp = parseFloatArray(image.data.string('x00200032'), 3)
      if (!iop) iop = parseFloatArray(image.data.string('x00200037'), 6)
      if (!spacing) spacing = parseFloatArray(image.data.string('x00280030'), 2)
    } catch (e) {
      /* ignore */
    }
  }

  const rows =
    (fromFile && fromFile.rows) ||
    fileOrImage.rows ||
    (image && image.rows) ||
    0
  const cols =
    (fromFile && fromFile.columns) ||
    fileOrImage.columns ||
    (image && image.columns) ||
    0

  if (!ipp || !iop || !rows || !cols) return null
  if (!spacing) spacing = [1, 1]

  return { ipp, iop, rows, cols, spacing }
}

/**
 * Intersección del plano del corte con el plano Scout → segmento en coords de píxel Scout.
 * @returns {{ x1: number, y1: number, x2: number, y2: number, index: number }|null}
 */
export function projectSliceLineOntoScout(scoutGeo, sliceGeo, index) {
  if (!scoutGeo || !sliceGeo) return null

  const sRow = norm([scoutGeo.iop[0], scoutGeo.iop[1], scoutGeo.iop[2]])
  const sCol = norm([scoutGeo.iop[3], scoutGeo.iop[4], scoutGeo.iop[5]])
  const sN = norm(cross(sRow, sCol))

  const vRow = [sliceGeo.iop[0], sliceGeo.iop[1], sliceGeo.iop[2]]
  const vCol = [sliceGeo.iop[3], sliceGeo.iop[4], sliceGeo.iop[5]]
  const vN = norm(cross(vRow, vCol))

  // Dirección de la línea de intersección
  const dir = cross(sN, vN)
  const dirLen = Math.sqrt(dot(dir, dir))
  if (dirLen < 1e-6) return null // planos paralelos
  const d = scale(dir, 1 / dirLen)

  // Punto en la intersección: proyectar IPP del corte sobre Scout
  const diff = sub(sliceGeo.ipp, scoutGeo.ipp)
  // Resolver punto p = scout.ipp + u*sRow + v*sCol que está en plano del slice
  // (p - slice.ipp) · vN = 0
  // Usamos línea parametrizada: hallar dos puntos en bordes del scout

  const rowSp = scoutGeo.spacing[0] || 1
  const colSp = scoutGeo.spacing[1] || 1

  // Convertir punto 3D a pixel scout.
  // DICOM: row cosines = eje horizontal (↑ column index / x);
  //        column cosines = eje vertical (↑ row index / y).
  const toPixel = (p3) => {
    const r = sub(p3, scoutGeo.ipp)
    const x = dot(r, sRow) / colSp
    const y = dot(r, sCol) / rowSp
    return { x, y }
  }

  // Hallar punto en la línea: punto en plano scout más cercano al IPP del slice
  // que también esté en el plano del slice.
  // p = slice.ipp + t * (vN × sN)? Mejor: p = scout.ipp + alpha*sRow + beta*sCol
  // con (p - slice.ipp)·vN = 0 y p en dirección d.
  // Tomamos p0 = proyección del slice IPP sobre el plano scout:
  const dist = dot(diff, sN)
  const p0 = sub(sliceGeo.ipp, scale(sN, dist))
  // Asegurar que p0 esté cerca del plano slice; ajustar a lo largo de d
  const residual = dot(sub(p0, sliceGeo.ipp), vN)
  // Mover a lo largo de (sN × d) no — mover a lo largo de d no cambia el plano scout.
  // Mover ortogonal a d dentro del scout:
  const inScout = cross(sN, d)
  const inLen = Math.sqrt(dot(inScout, inScout)) || 1
  const inDir = scale(inScout, 1 / inLen)
  const denom = dot(inDir, vN)
  let center = p0
  if (Math.abs(denom) > 1e-8) {
    center = sub(p0, scale(inDir, residual / denom))
  }

  // Extender bien más allá del FOV y recortar al rectángulo completo de la imagen
  const halfDiag =
    Math.sqrt(
      Math.pow(scoutGeo.cols * colSp, 2) + Math.pow(scoutGeo.rows * rowSp, 2)
    ) + 100
  const pA = add(center, scale(d, -halfDiag))
  const pB = add(center, scale(d, halfDiag))
  const a = toPixel(pA)
  const b = toPixel(pB)

  const clipped = extendLineToImageEdges(
    a.x,
    a.y,
    b.x,
    b.y,
    scoutGeo.cols,
    scoutGeo.rows
  )
  if (!clipped) return null

  return {
    x1: clipped.x1,
    y1: clipped.y1,
    x2: clipped.x2,
    y2: clipped.y2,
    index,
  }
}

/**
 * Extiende un segmento hasta los bordes del matrix de la imagen (borde a borde).
 * @returns {{ x1:number,y1:number,x2:number,y2:number }|null}
 */
export function extendLineToImageEdges(x1, y1, x2, y2, cols, rows) {
  const w = Number(cols) || 0
  const h = Number(rows) || 0
  if (!(w > 0) || !(h > 0)) return null

  let dx = x2 - x1
  let dy = y2 - y1
  let len = Math.hypot(dx, dy)
  if (len < 1e-9) {
    // Segmento degenerado: línea horizontal por el punto
    dx = 1
    dy = 0
    len = 1
  }
  const ux = dx / len
  const uy = dy / len
  const cx = (x1 + x2) / 2
  const cy = (y1 + y2) / 2
  const big = Math.hypot(w, h) * 2 + 10
  return clipLineToRect(
    cx - ux * big,
    cy - uy * big,
    cx + ux * big,
    cy + uy * big,
    0,
    0,
    w,
    h
  )
}

function clipLineToRect(x1, y1, x2, y2, minX, minY, maxX, maxY) {
  // Cohen–Sutherland
  const INSIDE = 0
  const LEFT = 1
  const RIGHT = 2
  const BOTTOM = 4
  const TOP = 8
  const code = (x, y) => {
    let c = INSIDE
    if (x < minX) c |= LEFT
    else if (x > maxX) c |= RIGHT
    if (y < minY) c |= TOP
    else if (y > maxY) c |= BOTTOM
    return c
  }

  let c1 = code(x1, y1)
  let c2 = code(x2, y2)
  let accept = false

  for (let i = 0; i < 20; i++) {
    if (!(c1 | c2)) {
      accept = true
      break
    }
    if (c1 & c2) break
    const cOut = c1 || c2
    let x = 0
    let y = 0
    if (cOut & TOP) {
      x = x1 + ((x2 - x1) * (minY - y1)) / (y2 - y1 || 1e-9)
      y = minY
    } else if (cOut & BOTTOM) {
      x = x1 + ((x2 - x1) * (maxY - y1)) / (y2 - y1 || 1e-9)
      y = maxY
    } else if (cOut & RIGHT) {
      y = y1 + ((y2 - y1) * (maxX - x1)) / (x2 - x1 || 1e-9)
      x = maxX
    } else {
      y = y1 + ((y2 - y1) * (minX - x1)) / (x2 - x1 || 1e-9)
      x = minX
    }
    if (cOut === c1) {
      x1 = x
      y1 = y
      c1 = code(x1, y1)
    } else {
      x2 = x
      y2 = y
      c2 = code(x2, y2)
    }
  }

  if (!accept) return null
  return { x1, y1, x2, y2 }
}

/**
 * @param {object} scoutFile primer frame scout
 * @param {object[]} volumeFiles stack volumétrico (p.ej. axial)
 * @returns {Array<{ x1:number,y1:number,x2:number,y2:number,index:number }>}
 */
export function buildScoutReferenceLines(scoutFile, volumeFiles) {
  const scoutGeo = readPlaneGeometry(scoutFile)
  if (!scoutGeo || !Array.isArray(volumeFiles)) return []

  const lines = []
  const step = volumeFiles.length > 80 ? Math.ceil(volumeFiles.length / 60) : 1
  for (let i = 0; i < volumeFiles.length; i += step) {
    const sliceGeo = readPlaneGeometry(volumeFiles[i])
    const line = projectSliceLineOntoScout(scoutGeo, sliceGeo, i)
    if (line) lines.push(line)
  }
  // Asegurar que el último índice esté incluido
  const last = volumeFiles.length - 1
  if (last > 0 && step > 1 && !lines.some((l) => l.index === last)) {
    const sliceGeo = readPlaneGeometry(volumeFiles[last])
    const line = projectSliceLineOntoScout(scoutGeo, sliceGeo, last)
    if (line) lines.push(line)
  }
  return lines
}

/**
 * Encuentra el índice de línea más cercano a un click en coords de imagen.
 */
export function hitTestReferenceLine(lines, imageX, imageY, thresholdPx = 8) {
  if (!Array.isArray(lines) || !lines.length) return null
  let best = null
  let bestDist = thresholdPx
  for (const line of lines) {
    const dist = pointToSegmentDistance(imageX, imageY, line.x1, line.y1, line.x2, line.y2)
    if (dist <= bestDist) {
      bestDist = dist
      best = line
    }
  }
  return best
}

function pointToSegmentDistance(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1
  const dy = y2 - y1
  const len2 = dx * dx + dy * dy
  if (len2 < 1e-9) return Math.hypot(px - x1, py - y1)
  let t = ((px - x1) * dx + (py - y1) * dy) / len2
  t = Math.max(0, Math.min(1, t))
  const qx = x1 + t * dx
  const qy = y1 + t * dy
  return Math.hypot(px - qx, py - qy)
}
